import React, { useRef, useState, useCallback, useEffect } from 'react'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import { useConfig } from '../ConfigProvider'
import cloneDeep from 'lodash/cloneDeep'
import FileItem from './FileItem'
import Button from '../Buttons'
import CloseButton from '../CloseButton'
import Notification from '../Notification'
import toast from '../toast'
import { useDispatch, useSelector } from 'react-redux'
import { deleteMedia, setHeicLoading } from 'demo/store/commonReducer'
import ConfirmDialog from '../../shared/ConfirmDialog'
import heic2any from 'heic2any'
import { IoConstructOutline } from 'react-icons/io5'

const filesToArray = (files) => Object.keys(files).map((key) => files[key])

const Upload = React.forwardRef((props, ref) => {
    const {
        customise,
        datatable,
        beforeAfter,
        beforeAfterStart,
        accept,
        beforeUpload,
        maxFileSize,
        disabled,
        disabledEdit,
        draggable,
        fileList,
        multiple,
        onChange,
        onFileRemove,
        showList,
        tip,
        uploadLimit,
        children,
        className,
        field,
        form,
        minHeight,
        showUploadTime,
        name,
        values,
        ...rest
    } = props

    const dispatch = useDispatch()
    const fileInputField = useRef(null)
    const [files, setFiles] = useState(fileList)
    const [dragOver, setDragOver] = useState(false)
    const [deleteId, setDeleteId] = useState('')
    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false)

    const { deleteLoading } = useSelector((state) => state.common)

    const { themeColor, primaryColorLevel } = useConfig()

    useEffect(() => {
        setFiles(fileList)
    }, [fileList])

    const triggerMessage = (msg) => {
        toast.push(
            <Notification type="danger" duration={2000}>
                {msg || 'Upload Failed!'}
            </Notification>,
            {
                placement: 'top-center',
            }
        )
    }

    const pushFile = (newFiles, file) => {
        for (let f of newFiles) {
            file.push(f)
        }
        return file
    }

    const addNewFiles = (newFiles) => {
        let file = cloneDeep(files)

        if (typeof uploadLimit === 'number' && uploadLimit !== 0) {
            if (Object.keys(file).length >= uploadLimit) {
                if (uploadLimit === 1) {
                    file.shift()
                    file = pushFile(newFiles, file)
                }

                return filesToArray({ ...file })
            }
        }
        file = pushFile(newFiles, file)

        return filesToArray({ ...file })
    }

    const checkUploadFile = (file) => {
        let valid = true

        const maxSize = maxFileSize

        for (let f of file) {
            if (!f.type?.startsWith('image/')) {
                valid = 'Please upload image files only!'
            }

            if (f.size >= maxSize) {
                valid = `Upload file size cannot more than ${
                    beforeAfterStart ? 5 : 2
                }MB!`
            }
        }

        return valid
    }

    const onNewFileUpload = async (e) => {
        const newFiles = Array.from(e.target?.files) || []
        let result = true
        let processedFiles = []

        if ((beforeAfterStart || beforeAfter) && values[name]) {
            const MAX_TOTAL_SIZE = beforeAfterStart
                ? 80 * 1024 * 1024
                : 25 * 1024 * 1024

            const totalSize = [...values[name], ...newFiles].reduce(
                (acc, file) => (file.size ? acc + file.size : acc + 0),
                0
            )

            if (totalSize > MAX_TOTAL_SIZE) {
                triggerMessage(
                    `Total files size should be less than ${
                        beforeAfterStart ? '80' : '25'
                    } MB`
                )
                return
            }
        }

        // `accept` is a STRING everywhere it is passed from a screen - the same
        // comma-separated list the native input takes, e.g. ".jpg,.jpeg,.png,.webp,.pdf"
        // - and one caller passes an array. This read it as an array either way, so
        // picking any file on a screen that passed the string crashed the dialog with
        // "allowedFileTypes.some is not a function" before the file was looked at.
        const allowedFileTypes = Array.isArray(accept)
            ? accept
            : typeof accept === 'string' && accept.trim() !== ''
            ? accept
                  .split(',')
                  .map((type) => type.trim())
                  .filter(Boolean)
            : ['image/*']

        for (let f of newFiles) {
            const fileMaxSize = maxFileSize || 2 * 1024 * 1024 // 2MB
            const maxResolution = 4500 // 4500 x 4500

            if (allowedFileTypes.length > 0) {
                const fileAccepted = allowedFileTypes.some((type) => {
                    if (type.startsWith('.')) {
                        return f.name.toLowerCase().endsWith(type.toLowerCase())
                    } else if (type.includes('/*')) {
                        return f.type.startsWith(type.split('/')[0])
                    } else {
                        return f.type === type || f.type.includes(type)
                    }
                })

                if (!fileAccepted) {
                    const readableAcceptTypes = allowedFileTypes
                        .map((type) => {
                            if (type.startsWith('.')) return type.toLowerCase()
                            if (type.includes('/*')) return type.split('/')[0]
                            return type.split('/')[1] || type
                        })
                        .join(', ')

                    triggerMessage(
                        `Please upload only ${readableAcceptTypes} files!`
                    )

                    return
                }
            }

            if (f.size > fileMaxSize) {
                const sizeInMB = (fileMaxSize / 1024 / 1024).toFixed(1)
                triggerMessage(
                    `Upload file size cannot more than ${sizeInMB}MB!`
                )

                return
            }

            if (f.type.startsWith('image/')) {
                // Safari CANNOT decode HEIC here - createImageBitmap rejects on the
                // very files an iPhone or iPad camera produces. Unguarded, that
                // rejection killed this whole handler before the conversion below
                // ever ran: no file, no message, nothing. A photo we cannot measure
                // is let through to be converted, and measured after.
                let imageBitmap = null

                try {
                    imageBitmap = await createImageBitmap(f)
                } catch (error) {
                    imageBitmap = null
                }

                if (
                    imageBitmap &&
                    (imageBitmap.width > maxResolution ||
                        imageBitmap.height > maxResolution)
                ) {
                    triggerMessage(
                        'Image resolution cannot more than 4500 x 4500 pixels!'
                    )

                    return
                }
            }
        }

        for (let f of newFiles) {
            const fileName = f.name.toLowerCase()

            // What an Apple camera actually hands over. `.heif` too - the same format
            // under its other extension.
            if (fileName.endsWith('.heic') || fileName.endsWith('.heif')) {
                dispatch(setHeicLoading(true))
                try {
                    const converted = await heic2any({
                        blob: f,
                        // JPEG, not PNG. These are photographs: the same shot as a
                        // lossless PNG runs many times larger and would be refused by
                        // the API's 10MB cap - and jpeg is what it stores anyway.
                        toType: 'image/jpeg',
                        quality: 0.9,
                    })

                    // A HEIC can hold more than one image, and heic2any answers with
                    // an ARRAY when it does. Wrapping that array in a File produced a
                    // file of "[object Blob]" that no decoder could open.
                    const blob = Array.isArray(converted) ? converted[0] : converted

                    processedFiles.push(
                        new File(
                            [blob],
                            f.name.replace(/\.hei[cf]$/i, '.jpg'),
                            { type: 'image/jpeg' }
                        )
                    )
                } catch (error) {
                    // It used to log to a console nobody has open on a tablet and drop
                    // the photo, so taking one did nothing at all.
                    triggerMessage(
                        'That photo could not be converted. Pick it from your Photo Library instead, or set Camera > Formats to Most Compatible.'
                    )
                } finally {
                    // ALWAYS. Only the success path cleared this, so one failed
                    // conversion left the tile reading "Loading.." for good - there is
                    // nothing else that ever sets it back.
                    dispatch(setHeicLoading(false))
                }
            } else {
                processedFiles.push(f)
            }
        }

        if (result && processedFiles.length > 0) {
            let updatedFiles = addNewFiles(processedFiles)
            // setFiles(updatedFiles);
            onChange?.(updatedFiles, files)
        }
    }

    const onDeleteConfirmation = (fileIndex, id) => {
        setDeleteId({ fileIndex, id })
        setDeleteConfirmationOpen(true)
    }

    const onDeleteConfirmationClose = () => {
        setDeleteConfirmationOpen(false)
    }

    const removeFile = (fileIndex) => {
        if (deleteId.id) {
            dispatch(deleteMedia({ id: deleteId.id }))
                .unwrap()
                .then((res) => {
                    onDeleteConfirmationClose()
                    toast.push(
                        <Notification type="success">
                            Selected attachment is successfully deleted
                        </Notification>
                    )

                    const deletedFileList = files.filter(
                        (_, index) => index !== deleteId.fileIndex
                    )
                    setFiles(deletedFileList)
                    onFileRemove?.(deletedFileList)
                })
                .catch((ex) => {
                    if (ex && ex.response?.status === 422) {
                        const errors = ex.response.data.errors
                        if (errors && Object.keys(errors).length > 0) {
                            Object.keys(errors).map((item, i) => {
                                toast.push(
                                    <Notification type="danger">
                                        {errors[item][0]}
                                    </Notification>
                                )
                            })
                        }
                    }
                })
        } else {
            const deletedFileList = files.filter(
                (_, index) => index !== fileIndex
            )
            setFiles(deletedFileList)
            onFileRemove?.(deletedFileList)
        }
    }

    const triggerUpload = (e) => {
        if (!disabled) {
            fileInputField.current?.click()
        }
        e.stopPropagation()
    }

    const renderChildren = () => {
        if (!draggable && !children) {
            return (
                <Button disabled={disabled} onClick={(e) => e.preventDefault()}>
                    Upload
                </Button>
            )
        }

        if (draggable && !children) {
            return <span>Choose a file or drag and drop here</span>
        }

        return children
    }

    const handleDragLeave = useCallback(() => {
        if (draggable) {
            setDragOver(false)
        }
    }, [draggable])

    const handleDragOver = useCallback(() => {
        if (draggable && !disabled) {
            setDragOver(true)
        }
    }, [draggable, disabled])

    const handleDrop = useCallback(() => {
        if (draggable) {
            setDragOver(false)
        }
    }, [draggable])

    const draggableProp = {
        onDragLeave: handleDragLeave,
        onDragOver: handleDragOver,
        onDrop: handleDrop,
    }

    const draggableEventFeedbackClass = `border-${themeColor}-${primaryColorLevel}`

    const uploadClass = classNames(
        'upload',
        draggable && `upload-draggable`,
        draggable && !disabled && `hover:${draggableEventFeedbackClass}`,
        draggable && disabled && 'disabled',
        dragOver && draggableEventFeedbackClass,
        className
    )

    const uploadInputClass = classNames(
        'upload-input',
        draggable && `draggable`
    )

    return (
        <>
            {((files.length === 0 && customise) || !customise) &&
                !disabledEdit && (
                    <div
                        ref={ref}
                        className={uploadClass}
                        style={minHeight ? { minHeight: minHeight } : {}}
                        {...(draggable
                            ? draggableProp
                            : { onClick: triggerUpload })}
                        {...rest}
                    >
                        <input
                            className={uploadInputClass}
                            type="file"
                            ref={fileInputField}
                            onChange={onNewFileUpload}
                            disabled={disabled}
                            multiple={multiple}
                            accept={accept}
                            title=""
                            value=""
                            {...field}
                            {...rest}
                        ></input>
                        {renderChildren()}
                    </div>
                )}
            {tip}
            {(!customise || (customise && files.length > 0)) && showList && (
                <div
                    className={`upload-file-list ${
                        (files.length > 0 && !minHeight) || disabledEdit
                            ? 'm-0'
                            : ''
                    } `}
                >
                    {files.map((file, index) => (
                        <FileItem
                            files={files}
                            index={index}
                            file={file}
                            key={file.name + index}
                            customise={customise}
                            datatable={datatable}
                            showUploadTime={showUploadTime}
                        >
                            {!disabledEdit && (
                                <CloseButton
                                    onClick={() =>
                                        file.image_id
                                            ? onDeleteConfirmation(
                                                  index,
                                                  file.image_id
                                              )
                                            : removeFile(index)
                                    }
                                    className={`upload-file-remove ${
                                        customise ? 'px-1' : ''
                                    }`}
                                />
                            )}
                        </FileItem>
                    ))}
                    <ConfirmDialog
                        isOpen={deleteConfirmationOpen}
                        onClose={onDeleteConfirmationClose}
                        onRequestClose={onDeleteConfirmationClose}
                        type="danger"
                        title="Remove image"
                        onCancel={onDeleteConfirmationClose}
                        onConfirm={removeFile}
                        confirmButtonColor="red-600"
                        disabled={deleteLoading}
                    >
                        <p> Are you sure you want to remove this image? </p>
                    </ConfirmDialog>
                </div>
            )}
        </>
    )
})

Upload.propTypes = {
    uploadLimit: PropTypes.number,
    draggable: PropTypes.bool,
    disabled: PropTypes.bool,
    showList: PropTypes.bool,
    multiple: PropTypes.bool,
    // A comma-separated string, as the native input takes, or a list of the same.
    accept: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string),
    ]),
    tip: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
}

Upload.defaultProps = {
    draggable: false,
    showList: true,
    disabled: false,
    fileList: [],
}

export default Upload
